import React, { useEffect, useId } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
import {
  Menu,
  IconButton,
  Box,
  VStack,
  Text,
  Portal,
  Link,
  Tooltip,
} from "@commonsku/design-system";
import { LuHistory } from "react-icons/lu";
import { capitalize } from "lodash";
import withReducers from "../../store/withReducers";
import recentProjectsSlice, {
  fetchRecentProjects,
  selectRecentProjects,
  selectRecentProjectsLoading,
  RecentProjectsRootState,
} from "../../redux/recentProjects";

const MAX_RECENT_PROJECTS = 5;

/**
 * Nav bar button that fetches the signed-in user's recently touched projects on
 * mount and shows them in a dropdown menu, per CORE-1912.
 */
function RecentProjectsButton() {
  const triggerId = useId();
  const dispatch =
    useDispatch<ThunkDispatch<RecentProjectsRootState, undefined, AnyAction>>();
  const projects = useSelector((state: RecentProjectsRootState) =>
    selectRecentProjects(state),
  );
  const loading = useSelector((state: RecentProjectsRootState) =>
    selectRecentProjectsLoading(state),
  );

  useEffect(() => {
    dispatch(fetchRecentProjects());
  }, [dispatch]);

  const recentProjects = projects.slice(0, MAX_RECENT_PROJECTS);

  return (
    <Menu.Root
      ids={{ trigger: triggerId }}
      positioning={{ placement: "bottom-end", gutter: 8 }}
    >
      <Tooltip.Root
        ids={{ trigger: triggerId }}
        positioning={{ placement: "bottom-end", strategy: "fixed" }}
      >
        <Tooltip.Trigger asChild>
          <Menu.Trigger asChild>
            <IconButton
              label="Recent projects"
              variant="outline"
              colorPalette="neutral"
              size="sm"
              rounded="md"
            >
              <LuHistory size={18} />
            </IconButton>
          </Menu.Trigger>
        </Tooltip.Trigger>
        <Tooltip.Positioner>
          <Tooltip.Content>Recent projects</Tooltip.Content>
        </Tooltip.Positioner>
      </Tooltip.Root>

      <Portal>
        <Menu.Positioner>
          <Menu.Content w="260px" p="8px" pr="20px">
            {recentProjects.length === 0 ? (
              <Box p="8px">
                <Text fontSize="sm" m="0" fontFamily="body">
                  {loading ? "Loading..." : "No recent projects"}
                </Text>
              </Box>
            ) : (
              recentProjects.map((project) => (
                <Menu.Item
                  key={project.job_id}
                  value={project.job_number}
                  p="6px"
                  cursor="pointer"
                  asChild
                >
                  <Link
                    href={`/project/${project.job_number}`}
                    display="block"
                    color="inherit"
                    _hover={{ textDecoration: "none", bg: "gray.100" }}
                  >
                    <VStack align="start" gap="0" overflow="hidden">
                      <Text
                        fontSize="sm"
                        m="0"
                        fontWeight="bold"
                        fontFamily="body"
                      >
                        {project.form_number}
                      </Text>
                      <Text fontSize="xs" m="0" fontFamily="body">
                        <Text as="span" fontWeight="bold" fontFamily="body">
                          {capitalize(project.order_type)}
                        </Text>{" "}
                        - {project.job_name}
                      </Text>
                    </VStack>
                  </Link>
                </Menu.Item>
              ))
            )}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}

export default withReducers(RecentProjectsButton, {
  [recentProjectsSlice.name]: recentProjectsSlice.reducer,
});
